import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { AxiosError } from "axios";

import { fetchProductionGroupAliasByMachineGroup } from "@/api/selection/production-group-api";
import { emptyGuid } from "@/constants";
import { useMachineGroups } from "@/hooks/machine-groups";
import { useCorrugates } from "@/hooks/corrugates";
import { useCreateProductionGroup, useProductionGroups, useUpdateProductionGroup } from "@/hooks/production-groups";
import {
	useCreateSelectionPipeline,
	useSelectionPipelines,
	useUpdateSelectionPipeline,
} from "@/hooks/selection-pipelines";
import { type ProductionGroupConfiguration } from "@/schemas";
import type { ProductionGroup } from "@/schemas/production-group";
import type { SelectionDataPipeline } from "@/schemas/routing/selection-pipeline";
import { buildEmptyPipeline } from "@/utils/selection-pipeline";

import { scanToProduceOptions } from "../manual-scan-to-produce-dropdown";
import type { ProductionGroupLookup } from "../types";

import { ProductionGroupsConfigurationModalTemplate } from "./production-groups-configuration-modal.template";

export interface ProductionGroupsConfigurationModalProps {
	readonly open: boolean;
	readonly productionGroup?: ProductionGroup;
	readonly onClose: () => void;
}

export const ProductionGroupsConfigurationModal: React.FC<ProductionGroupsConfigurationModalProps> = ({
	open,
	productionGroup,
	onClose,
}) => {
	const intl = useIntl();

	const { machineGroups: machineGroupsData } = useMachineGroups({ refetchInterval: false });
	const { corrugates: corrugatesData } = useCorrugates({ refetchInterval: false });
	const { productionGroups: productionGroupsData } = useProductionGroups({ refetchInterval: false });
	const { data: pipelinesData } = useSelectionPipelines();

	const { mutate: createProductionGroup } = useCreateProductionGroup();
	const { mutate: updateProductionGroup } = useUpdateProductionGroup();
	const { mutate: createSelectionPipeline } = useCreateSelectionPipeline();
	const { mutate: updateSelectionPipeline } = useUpdateSelectionPipeline();

	const [isSaving, setIsSaving] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
	const [productionGroupLookup, setProductionGroupLookup] = useState<ProductionGroupLookup>({});

	const machineGroups = useMemo(() => machineGroupsData ?? [], [machineGroupsData]);
	const corrugates = useMemo(
		() => [...(corrugatesData ?? [])].sort((a, b) => (a.alias > b.alias ? 1 : -1)),
		[corrugatesData],
	);
	const productionGroups = useMemo(() => productionGroupsData ?? [], [productionGroupsData]);
	const pipelines = useMemo(() => pipelinesData ?? [], [pipelinesData]);

	const originalConfiguredMgs = useMemo(
		() => productionGroup?.configuredMachineGroups ?? [],
		[productionGroup?.configuredMachineGroups],
	);

	useEffect(() => {
		if (!open || machineGroups.length === 0) return;

		let cancelled = false;

		const loadProductionGroupLookup = async (): Promise<void> => {
			const lookup: ProductionGroupLookup = {};
			for (const machineGroup of machineGroups) {
				const alias = await fetchProductionGroupAliasByMachineGroup(machineGroup.id);
				if (alias !== undefined) {
					lookup[machineGroup.id] = alias;
				}
			}

			if (!cancelled) {
				setProductionGroupLookup(lookup);
			}
		};

		loadProductionGroupLookup().catch(() => {
			/* Assigned-to display is best-effort; ignore lookup failures. */
		});

		return () => {
			cancelled = true;
		};
	}, [open, machineGroups]);

	const machineGroupSelectable = useMemo(() => {
		return machineGroups.reduce<Record<string, boolean>>((acc, machineGroup) => {
			if (machineGroup.productionEnabled) {
				acc[machineGroup.id] = false;
				return acc;
			}

			if (originalConfiguredMgs.includes(machineGroup.id)) {
				acc[machineGroup.id] = true;
				return acc;
			}

			acc[machineGroup.id] = !productionGroups.some((pg) => pg.configuredMachineGroups.includes(machineGroup.id));
			return acc;
		}, {});
	}, [machineGroups, productionGroups, originalConfiguredMgs]);

	const defaultValues = useMemo((): ProductionGroupConfiguration => {
		if (!productionGroup) {
			return {
				alias: "",
				description: "",
				configuredMachineGroups: [],
				configuredCorrugates: [],
				manualScanToProduce: scanToProduceOptions.none,
				pipeline: buildEmptyPipeline(),
			};
		}

		const matchingPipeline = pipelines.find((pipeline) => pipeline.id === productionGroup.pipelineId);
		const pipeline = matchingPipeline?.dataPipeline
			? structuredClone(matchingPipeline.dataPipeline)
			: buildEmptyPipeline();

		return {
			alias: productionGroup.alias,
			description: productionGroup.description ?? "",
			configuredMachineGroups: productionGroup.configuredMachineGroups,
			configuredCorrugates: productionGroup.configuredCorrugates ?? [],
			manualScanToProduce: productionGroup.manualScanToProduce ?? scanToProduceOptions.none,
			pipeline,
		};
	}, [productionGroup, pipelines]);

	const getErrorMessage = (error: unknown, alias: string): string => {
		if (error instanceof AxiosError && error.status === 409) {
			if (error.response?.data === "UniqueTenantAlias") {
				return intl.formatMessage(
					{
						id: "Failed to save Production Group: An item already exists with the name {name}",
						defaultMessage: "Failed to save Production Group: An item already exists with the name {name}",
					},
					{ name: alias },
				);
			}

			return intl.formatMessage({
				id: "Failed to save Production Group: A selected machine group already belongs to another Production Group",
				defaultMessage:
					"Failed to save Production Group: A selected machine group already belongs to another Production Group",
			});
		}

		return intl.formatMessage({
			id: "Failed to save Production Group",
			defaultMessage: "Failed to save Production Group",
		});
	};

	const handleSubmit = async (data: ProductionGroupConfiguration): Promise<void> => {
		setIsSaving(true);
		setErrorMessage(undefined);

		try {
			const pipelineData: SelectionDataPipeline = { ...data.pipeline, name: data.alias };

			if (productionGroup) {
				let pipelineId = productionGroup.pipelineId;

				if (pipelineId && pipelineId !== emptyGuid) {
					const matchingPipeline = pipelines.find((pipeline) => pipeline.id === pipelineId);
					await updateSelectionPipeline({
						id: pipelineId,
						tenantId: matchingPipeline?.tenantId ?? productionGroup.tenantId,
						dataPipeline: pipelineData,
					});
				} else {
					const pipelineResponse = await createSelectionPipeline({ dataPipeline: pipelineData });
					pipelineId = pipelineResponse.id;
				}

				await updateProductionGroup({
					...productionGroup,
					alias: data.alias,
					description: data.description,
					configuredMachineGroups: data.configuredMachineGroups,
					configuredCorrugates: data.configuredCorrugates,
					manualScanToProduce: data.manualScanToProduce,
					pipelineId,
				});
			} else {
				const pipelineResponse = await createSelectionPipeline({ dataPipeline: pipelineData });

				await createProductionGroup({
					alias: data.alias,
					description: data.description,
					configuredMachineGroups: data.configuredMachineGroups,
					configuredCorrugates: data.configuredCorrugates,
					manualScanToProduce: data.manualScanToProduce,
					pipelineId: pipelineResponse.id,
				});
			}

			onClose();
		} catch (e) {
			setErrorMessage(getErrorMessage(e, data.alias));
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<ProductionGroupsConfigurationModalTemplate
			open={open}
			productionGroup={productionGroup}
			defaultValues={defaultValues}
			machineGroups={machineGroups}
			corrugates={corrugates}
			machineGroupSelectable={machineGroupSelectable}
			machineGroupAssignedTo={productionGroupLookup}
			errorMessage={errorMessage}
			isSaving={isSaving}
			onSubmit={handleSubmit}
			onClose={onClose}
		/>
	);
};
